import type { Tables } from './database.types.js';

export type Member = Tables<'members'>;

/** Up to two initials for an avatar fallback: "Ada Lovelace" -> "AL". */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]! : '';
  return (first[0]! + (last[0] ?? first[1] ?? '')).toUpperCase();
}

/** Nicknames: lower-case letters, digits, ".", "_" and "-", 2 to 30 long (as the database checks). */
export const NICKNAME_PATTERN = /^[a-z0-9][a-z0-9_.-]{1,29}$/;

/** A nickname as typed, tidied: no "@", lower-case, trimmed. */
export function normalizeNickname(value: string): string {
  return value.trim().replace(/^@/, '').toLowerCase();
}

/** Why a (normalized) nickname won't do, or null. Empty means none. */
export function nicknameProblem(nickname: string): string | null {
  if (!nickname) return null;
  if (nickname.length < 2) return 'At least 2 characters.';
  if (nickname.length > 30) return 'At most 30 characters.';
  if (!NICKNAME_PATTERN.test(nickname)) return 'Use a–z, 0–9, ".", "_" or "-", starting with a letter or digit.';
  return null;
}

/**
 * The member someone meant by "@jono", "@edweird", "jono@..." or "Jono Smith":
 * an exact email, then a nickname, whole name, first name or email name, then a
 * unique prefix of one. Null when nobody or more than one member matches.
 */
export function matchMember<M extends Pick<Member, 'display_name' | 'email'> & { nickname?: string | null }>(
  query: string,
  members: readonly M[],
): M | null {
  const q = query.trim().toLowerCase().replace(/^@/, '');
  if (!q) return null;
  const keys = (m: M) => {
    const name = m.display_name.toLowerCase();
    return [name, name.split(/\s+/)[0] ?? name, m.email.split('@')[0] ?? m.email, ...(m.nickname ? [m.nickname] : [])];
  };
  const byEmail = members.filter((m) => m.email === q);
  if (byEmail.length === 1) return byEmail[0]!;
  const exact = members.filter((m) => keys(m).includes(q));
  if (exact.length === 1) return exact[0]!;
  if (exact.length > 1) return null;
  const prefix = members.filter((m) => keys(m).some((k) => k.startsWith(q)));
  return prefix.length === 1 ? prefix[0]! : null;
}
